import type { StateCreator } from 'zustand'
import type { GeoPlacement } from '@viewer/lib/geoTransform'
import type { FilterQuery } from '@viewer/lib/filter'

/** Uma origem posta em foco pelo "Ir": a do ficheiro IFC de um modelo
 *  ('ficheiro') ou a de um nó espacial (o expressID do IfcSite,
 *  IfcBuilding, IfcBuildingStorey ou IfcSpace). */
export interface OrigemEmFoco {
  modelId: string
  chave: 'ficheiro' | number
  /** Para o aviso no 3D: "Origem do ficheiro IFC · Casa.ifc". */
  rotulo: string
}

export type IfcSchemaVersion =
  | 'IFC2X3'
  | 'IFC4'
  | 'IFC4X1'
  | 'IFC4X2'
  | 'IFC4X3'

export interface LoadedModel {
  id: string
  fileName: string
  schemaVersion: IfcSchemaVersion | null
  visible: boolean
  entityCount: number
  /** SHA-256 of the IFC bytes, hex-encoded. Used by BCF to identify
   * the model a viewpoint depends on without uploading the file. */
  sha256: string
  /** Source used to create the live Fragments model. Optional for session
   * manifests written before native Fragments support. */
  sourceFormat?: 'ifc' | 'frag'
  /** False for a standalone `.frag`, where IFC-only editing is unavailable. */
  rawIfcAvailable?: boolean
}

export interface ViewerSlice {
  models: LoadedModel[]
  activeModelId: string | null
  selectedExpressId: number | null
  // Per-element visibility state. `hiddenCount` is the total number of
  // geometric items (across all loaded models) currently hidden via
  // hide/isolate; only used to drive the "Mostrar tudo" button enabled
  // state and the topbar badge. `ghostMode` makes non-selected items
  // render with a translucent grey overlay so the selection stands
  // out against context. Both are independent of model.object.visible
  // (the per-model eye toggle in the sidebar).
  hiddenCount: number
  ghostMode: boolean
  // 0..0.5: applied to the highlighter "ghost" style. 0.12 reads as
  // a clear context layer; pushing toward 0.5 makes non-selected
  // elements almost solid grey.
  ghostOpacity: number
  /** Como fica o resto do modelo no fantasma: só os contornos (as faces
   *  escondem-se, as arestas ficam esbatidas) ou translúcido. Ver
   *  viewer/lib/ghostContornos.ts. */
  ghostEstilo: 'contornos' | 'translucido'
  /** Modelos inteiros em fantasma, sem precisar de selecção (fantasma
   *  fixo). Ver viewerApi.setModeloEmFantasma. */
  modelosEmFantasma: string[]
  setModelosEmFantasma: (ids: string[]) => void
  /** Como se mostra um conflito ou um tópico BCF: cores, fantasma e
   *  caixa de corte, cada um à parte (viewer/realce/ControlosDoRealce). */
  realce: { cores: boolean; fantasma: boolean; caixa: boolean }
  setRealce: (patch: Partial<{ cores: boolean; fantasma: boolean; caixa: boolean }>) => void
  // Number of items currently rendered with the "fade" highlighter
  // style (selective transparency). Independent from ghost: fade is
  // an opt-in per-element override, ghost is an automatic context
  // dimmer that follows the selection.
  transparentCount: number
  transparencyOpacity: number
  // Tracks the "current visibility command" so the ribbon can render
  // Esconder / Isolar as toggles. Both modes coexist with the
  // hiddenCount machinery (which is just a counter); this field
  // remembers WHICH command produced the current hidden set.
  visibilityMode: 'normal' | 'hide' | 'isolate'
  // Scene-wide toggles persisted between sessions.
  gridVisible: boolean
  edgesVisible: boolean
  // Threshold (degrees) passed to THREE.EdgesGeometry. Lower → more
  // edges visible (including tessellation seams in curved geometry).
  // Higher → fewer, only at sharp transitions. 30° is a sane default;
  // exposed in Diagnóstico so the user can dial it for HVAC / MEP
  // models with finely-tessellated round fittings.
  edgeThreshold: number
  // Per-class default colour palette overlay applied to every loaded
  // model. Compensates for IFCs that ship without IfcSurfaceStyles
  // (very common for HVAC / MEP exports) by tinting elements by IFC
  // class, same visual convention as BIMvision / Solibri / Bonsai.
  classColorsEnabled: boolean
  // Scene background: dark for the BIM-tool default, light for
  // documentation screenshots / lighter spaces.
  bgMode: 'dark' | 'light'
  // Fundo em degradê e luz de hemisfério, para imagens e vídeos. Só
  // com o fundo claro.
  estiloApresentacao: boolean
  // Hide spatial volumes (IfcSpace, IfcZone, IfcSpatialZone) in 3D
  // even though they remain in the spatial tree / Tabela. Defaults
  // to true because spaces obscure the geometry on load.
  hideSpatialVolumes: boolean
  // Floating "Aparência" panel: paint custom colours, ghost slider,
  // transparency slider, all in one draggable window so the user
  // can keep it open while clicking elements.
  appearancePanelOpen: boolean
  appearancePanelPosition: { x: number; y: number }
  // Global UI zoom (panels, popups, ribbon, properties, menus). The
  // 3D viewer canvas opts out so picking math stays in CSS pixels;
  // see WorkspaceLayout where the viewer container forces zoom: 1.
  // Allowed range 0.85..1.6, in 0.05 steps. 1 = baseline.
  uiScale: number
  // Painel de propriedades: vista simples (default) vs avançada. A
  // vista simples mostra só property sets, quantity sets, classificações
  // e coordenadas; a avançada acrescenta atributos, relações inversas,
  // tipo IFC, geometria e JSON cru. Persistido.
  propsAdvanced: boolean
  // Left sidebar tab: which IFC index the user is browsing.
  //   spatial  - IfcProject/Site/Building/Storey/Space tree (default)
  //   logical  - IfcSystem / IfcGroup / IfcZone / IfcElementAssembly
  //   entities - flat list of every IFC entity present in the model
  leftSidebarTab: 'spatial' | 'logical' | 'entities' | 'types'
  // Revit-style overlays. Levels = horizontal outline at every
  // IfcBuildingStorey.Elevation; Grids = IfcGrid axis polylines.
  // Computed from raw IFC, drawn on the world scene root via
  // viewer/lib/levelsAndGrids.ts. Persisted across sessions.
  levelsOverlay: boolean
  gridsOverlay: boolean
  // Origin marker overlay: axes + label at the world origin (0,0,0)
  // and at each model's local origin (post MapConversion / override).
  // Drawn via viewer/lib/originMarker.ts. Persisted.
  originMarkerOverlay: boolean
  // Uma origem isolada pelo "Ir" (Info do modelo > Origens): o marcador
  // desenha só essa, com as coordenadas na etiqueta. null = todas as que
  // o filtro deixa. Não se grava: é um passo de inspecção.
  originMarkerFocus: OrigemEmFoco | null
  // Optional whitelist: when non-empty, the Grids overlay only
  // renders IfcGrids contained (via IfcRelContainedInSpatialStructure)
  // in one of these IfcBuildingStorey expressIDs. Empty array = all.
  // Stored per-session (no localStorage); when the model unloads we
  // clear it.
  gridsStoreyFilter: number[]
  // "Colorir por critério" floating panel: pick entity / system /
  // property and auto-assign a unique colour per bucket.
  colorByPanelOpen: boolean
  // Active "Colorir por critério" criterion. null when no criterion
  // is applied. The paint engine reads this on every model's
  // onViewUpdated and re-applies the colouring so it survives
  // tile spawns. A new IFC loaded after a criterion is set picks
  // up the same criterion automatically.
  colorByActive: ColorByCriterion | null
  // "Filtro" floating panel: build a multi-criterion query (AND/OR) to
  // pick elements by entity / storey / property / etc. Apply pushes
  // the matches as the viewer's selection.
  filterPanelOpen: boolean
  // Pré-preenchimento do Filtro: uma query que o painel adota ao abrir
  // (e depois limpa). Usado pelo dicionário de propriedades (Info do
  // modelo) para abrir o Filtro já apontado a um Pset.Propriedade.
  filterPrefill: FilterQuery | null
  // Geo-federação (Onda B). Para cada modelo guardamos:
  //   baselines: o placement auto-derivado do IfcMapConversion no
  //              load (ou identidade se o modelo não declarou
  //              MapConversion). Usado pela UI como referência para
  //              o botão "Reset".
  //   overrides: placement manualmente introduzido pelo utilizador
  //              no painel "Posição". Quando presente, substitui o
  //              baseline. Ausente = usa baseline.
  /** Rotação de APRESENTAÇÃO, em graus, em torno da vertical.
   *
   *  Roda o que as vistas padrão (Frente, Trás, Dir, Esq) querem dizer,
   *  para se poder olhar um edifício alinhado com o ecrã. É o "norte do
   *  projeto".
   *
   *  NÃO é georreferenciação e não tem nada a ver com o
   *  `GeoPlacement.rotationDeg`, que é o "norte verdadeiro" e é o que
   *  vai para o XAxisAbscissa do IfcMapConversion. Confundi-los grava
   *  IFC com o norte errado.
   *
   *  NÃO é por modelo, de propósito: é da vista. Se fosse por modelo,
   *  rodar um deles desalinhava uma federação de modelos
   *  georreferenciados. Sendo da vista, nada se move em relação a nada,
   *  porque nenhuma matriz de modelo é tocada. A bússola (N, E, S, O)
   *  continua a apontar para o mundo real e não roda com isto. */
  displayBearingDeg: number
  setDisplayBearing: (deg: number) => void
  /** A traçar a linha que define a horizontal do ecrã: null = desligado,
   *  array vazio = à espera do 1.º ponto, com um ponto = à espera do
   *  2.º. Mesmo desenho do `sectionAlignMode`. */
  bearingPick: [number, number, number][] | null
  setBearingPick: (pts: [number, number, number][] | null) => void
  geoBaselines: Record<string, GeoPlacement>
  geoOverrides: Record<string, GeoPlacement>
  // Undo de um nível do override de posição: o placement ANTERIOR de cada
  // modelo, guardado pelo setGeoOverride sempre que a posição muda
  // (alinhar/assentar/reposicionar/editor). "Desfazer" restaura-o; o
  // Reset (clearGeoOverride) e o removeModel limpam-no. Ausente = nada a
  // desfazer.
  geoUndo: Record<string, GeoPlacement>
  // CRS (sistema de coordenadas projectado) por modelo. `crsMeta` é o
  // nome lido do IfcProjectedCRS no load (ex.: "EPSG:3763"), null
  // quando o IFC não declara. `crsOverrides` é o código definido pelo
  // utilizador no editor de posição (só na sessão do viewer; não
  // escreve no IFC). Efetivo = override ?? meta.
  crsMeta: Record<string, string | null>
  crsOverrides: Record<string, string>
  // Offset "baked" detetado: modelos SEM IfcMapConversion cujas
  // coordenadas grandes vinham embutidas nos placements (LoGeoRef 30,
  // má prática em qualquer versão do IFC). O conversor recentra a geometria e devolve a
  // translação em getCoordinates(); guardamos aqui o placement que
  // restaura a posição verdadeira, para a UI oferecer "Usar como
  // posição" com um clique.
  geoDetectedBaked: Record<string, GeoPlacement>
  setGeoDetectedBaked: (modelId: string, placement: GeoPlacement) => void
  /** Distância (m) da geometria à origem do ficheiro, para os modelos com
   *  coordenadas grandes (viewer/lib/georef/coordenadasGrandes.ts). */
  coordenadasGrandes: Record<string, number>
  setCoordenadasGrandes: (modelId: string, distanciaM: number) => void
  setActiveModel: (id: string | null) => void
  setSelectedExpressId: (id: number | null) => void
  addModel: (model: LoadedModel) => void
  removeModel: (id: string) => void
  toggleModelVisibility: (id: string) => void
  setModelVisibility: (id: string, visible: boolean) => void
  /** Preenchido depois de o modelo carregar. Nascia sempre a zero e
   *  ninguém o actualizava: a barra de estado e o Documento liam-no e
   *  diziam "0 entidades" sobre um modelo cheio. */
  setModelEntityCount: (id: string, entityCount: number) => void
  setHiddenCount: (count: number) => void
  setGhostMode: (on: boolean) => void
  setGhostOpacity: (value: number) => void
  setGhostEstilo: (estilo: 'contornos' | 'translucido') => void
  setTransparentCount: (count: number) => void
  setTransparencyOpacity: (value: number) => void
  setVisibilityMode: (mode: 'normal' | 'hide' | 'isolate') => void
  setGridVisible: (on: boolean) => void
  setEdgesVisible: (on: boolean) => void
  setEdgeThreshold: (deg: number) => void
  setClassColorsEnabled: (on: boolean) => void
  setBgMode: (mode: 'dark' | 'light') => void
  setEstiloApresentacao: (on: boolean) => void
  setHideSpatialVolumes: (on: boolean) => void
  setAppearancePanelOpen: (on: boolean) => void
  setAppearancePanelPosition: (p: { x: number; y: number }) => void
  setUiScale: (value: number) => void
  setPropsAdvanced: (on: boolean) => void
  setLeftSidebarTab: (tab: 'spatial' | 'logical' | 'entities' | 'types') => void
  setLevelsOverlay: (on: boolean) => void
  setGridsOverlay: (on: boolean) => void
  setOriginMarkerOverlay: (on: boolean) => void
  setOriginMarkerFocus: (foco: OrigemEmFoco | null) => void
  setGridsStoreyFilter: (ids: number[]) => void
  toggleGridStorey: (id: number) => void
  setColorByPanelOpen: (on: boolean) => void
  setColorByActive: (c: ColorByCriterion | null) => void
  setFilterPanelOpen: (on: boolean) => void
  setFilterPrefill: (q: FilterQuery | null) => void
  setGeoBaseline: (modelId: string, placement: GeoPlacement) => void
  setGeoOverride: (modelId: string, placement: Partial<GeoPlacement>) => void
  clearGeoOverride: (modelId: string) => void
  /** Desfaz o último override de posição deste modelo (um nível),
   *  restaurando o placement anterior guardado em geoUndo. */
  undoGeoOverride: (modelId: string) => void
  setCrsMeta: (modelId: string, crsName: string | null) => void
  setCrsOverride: (modelId: string, crsCode: string) => void
  clearCrsOverride: (modelId: string) => void
  // Painel de mapa (OpenStreetMap via Leaflet) sobre o canvas.
  mapPanelOpen: boolean
  setMapPanelOpen: (open: boolean) => void
  // Mapa numa janela do browser (outro ecrã). Não se guarda: ao reabrir a
  // aplicação não há gesto e o browser bloquearia a janela. Fechar o
  // painel esquece-o.
  mapPanelDestacado: boolean
  setMapPanelDestacado: (v: boolean) => void
  // Acordeão "Ferramentas" (elevação, Google Earth, federação) no painel
  // Mapa: aberto/fechado. Persistido (default fechado).
  mapToolsOpen: boolean
  setMapToolsOpen: (open: boolean) => void
  // Pedido para abrir o editor de posição de um modelo no sidebar
  // (disparado de fora, ex.: clique na etiqueta do marcador de origem).
  // O Sidebar consome e limpa.
  placementEditorRequest: string | null
  requestPlacementEditor: (modelId: string) => void
  clearPlacementEditorRequest: () => void
  // Chão de mapa OSM no 3D (tiles por baixo do modelo georreferenciado).
  basemap3dOn: boolean
  setBasemap3dOn: (on: boolean) => void
  // Elevação (h, metros no mundo) do chão OSM. null = automática
  // (mínimo do bounding box do modelo).
  basemapGroundH: number | null
  setBasemapGroundH: (h: number | null) => void
  // Que categorias de origem o marcador desenha. Os pisos/espaços de um
  // edifício partilham a mesma vertical e empilham etiquetas, por isso
  // vêm desligados por defeito; a origem da cena (0,0,0) é um conceito
  // interno e também vem desligada. `labels` liga/desliga todo o texto
  // (as origens ficam só com a geometria dos tripés).
  originMarkerKinds: {
    world: boolean
    file: boolean
    site: boolean
    building: boolean
    storey: boolean
    space: boolean
    labels: boolean
  }
  setOriginMarkerKind: (
    kind:
      | 'world'
      | 'file'
      | 'site'
      | 'building'
      | 'storey'
      | 'space'
      | 'labels',
    on: boolean,
  ) => void
  // Relevo (DEM) no chão OSM 3D: alturas reais do terreno (terrarium).
  basemapTerrainOn: boolean
  setBasemapTerrainOn: (on: boolean) => void
  // Extensão do chão OSM: multiplicador da grelha de tiles (1 = 4×4,
  // 2 = 8×8, 3 = 12×12): mesma resolução, mais mapa à volta.
  basemapExtent: number
  setBasemapExtent: (extent: number) => void
}

export type ColorByMode =
  | 'entity'
  | 'predefinedType'
  | 'system'
  | 'material'
  | 'type'
  | 'property'

export interface ColorByCriterion {
  mode: ColorByMode
  /** Only set when mode === 'property'. Syntax: "Pset_X.Y" or "Name". */
  propertyKey?: string
}

const GRID_KEY = 'vn2r.gridVisible'
const EDGES_KEY = 'vn2r.edgesVisible'
const EDGE_THRESHOLD_KEY = 'vn2r.edgeThreshold'
const EDGE_THRESHOLD_MIN = 5
const EDGE_THRESHOLD_MAX = 80
const EDGE_THRESHOLD_DEFAULT = 30

function loadEdgeThreshold(): number {
  try {
    const v = localStorage.getItem(EDGE_THRESHOLD_KEY)
    if (v === null) return EDGE_THRESHOLD_DEFAULT
    const n = Number(v)
    if (!Number.isFinite(n)) return EDGE_THRESHOLD_DEFAULT
    return Math.min(EDGE_THRESHOLD_MAX, Math.max(EDGE_THRESHOLD_MIN, n))
  } catch {
    return EDGE_THRESHOLD_DEFAULT
  }
}
// v4: default flipped back to OFF. Fragments DOES carry the IFC's
// IfcSurfaceStyle materials per item (BIMMaterial = MeshLambertMaterial
// per element). Applying the palette via setColor was overriding those
// native colours globally. With the toggle off, an IFC with surface
// styles renders with its real colours; an IFC without falls back to
// the default Fragments material, and the user can toggle the palette
// ON for the uniform BIMvision-like discipline look.
const CLASS_COLORS_KEY = 'vn2r.classColorsEnabled.v4'
const GHOST_OPACITY_KEY = 'vn2r.ghostOpacity'
const GHOST_ESTILO_KEY = 'vn2r.ghostEstilo'
const REALCE_KEY = 'vn2r.realce'
const REALCE_POR_OMISSAO = { cores: true, fantasma: true, caixa: false }

function loadRealce(): { cores: boolean; fantasma: boolean; caixa: boolean } {
  try {
    const v = JSON.parse(localStorage.getItem(REALCE_KEY) ?? 'null') as Record<string, unknown> | null
    if (!v) return { ...REALCE_POR_OMISSAO }
    return {
      cores: typeof v.cores === 'boolean' ? v.cores : REALCE_POR_OMISSAO.cores,
      fantasma: typeof v.fantasma === 'boolean' ? v.fantasma : REALCE_POR_OMISSAO.fantasma,
      caixa: typeof v.caixa === 'boolean' ? v.caixa : REALCE_POR_OMISSAO.caixa,
    }
  } catch {
    return { ...REALCE_POR_OMISSAO }
  }
}
const TRANSPARENCY_OPACITY_KEY = 'vn2r.transparencyOpacity'
const BG_MODE_KEY = 'vn2r.bgMode'
const ESTILO_APRESENTACAO_KEY = 'vn2r.estiloApresentacao'
const HIDE_SPATIAL_KEY = 'vn2r.hideSpatialVolumes'
const UI_SCALE_KEY = 'vn2r.uiScale'
const UI_SCALE_MIN = 0.85
const UI_SCALE_MAX = 1.6
const PROPS_ADVANCED_KEY = 'vn2r.props.advanced'
const MAP_TOOLS_KEY = 'vn2r.map.tools'
const LEFT_TAB_KEY = 'vn2r.leftSidebarTab'
const LEVELS_KEY = 'vn2r.levelsOverlay'
const GRIDS_KEY = 'vn2r.gridsOverlay'
const ORIGIN_MARKER_KEY = 'vn2r.originMarker'
const ORIGIN_KINDS_KEY = 'vn2r.originMarkerKinds'
const BASEMAP_EXTENT_KEY = 'vn2r.basemapExtent'
const BASEMAP_TERRAIN_KEY = 'vn2r.basemapTerrainOn'

type OriginMarkerKinds = {
  world: boolean
  file: boolean
  site: boolean
  building: boolean
  storey: boolean
  space: boolean
  labels: boolean
}

const ORIGIN_KINDS_DEFAULT: OriginMarkerKinds = {
  world: false,
  file: true,
  site: false,
  building: false,
  storey: false,
  space: false,
  labels: true,
}

/** Lê originMarkerKinds do localStorage, tolerante: cada chave em falta
 *  ou não-booleana cai no default. */
function loadOriginKinds(): OriginMarkerKinds {
  try {
    const raw = localStorage.getItem(ORIGIN_KINDS_KEY)
    if (!raw) return { ...ORIGIN_KINDS_DEFAULT }
    const parsed = JSON.parse(raw) as Record<string, unknown>
    const out = { ...ORIGIN_KINDS_DEFAULT }
    for (const k of Object.keys(out) as Array<keyof OriginMarkerKinds>) {
      if (typeof parsed[k] === 'boolean') out[k] = parsed[k] as boolean
    }
    return out
  } catch {
    return { ...ORIGIN_KINDS_DEFAULT }
  }
}

function loadBasemapExtent(): number {
  try {
    const n = Number(localStorage.getItem(BASEMAP_EXTENT_KEY))
    if (Number.isFinite(n) && n >= 1 && n <= 3) return Math.round(n)
    return 1
  } catch {
    return 1
  }
}

function loadLeftSidebarTab(): 'spatial' | 'logical' | 'entities' | 'types' {
  try {
    const v = localStorage.getItem(LEFT_TAB_KEY)
    if (v === 'logical' || v === 'entities' || v === 'types') return v
    return 'spatial'
  } catch {
    return 'spatial'
  }
}

function loadOpacity(key: string, fallback: number): number {
  try {
    const v = localStorage.getItem(key)
    if (v === null) return fallback
    const n = Number(v)
    if (!Number.isFinite(n)) return fallback
    return Math.min(0.9, Math.max(0.05, n))
  } catch {
    return fallback
  }
}

function loadGhostOpacity(): number {
  return Math.min(0.5, loadOpacity(GHOST_OPACITY_KEY, 0.12))
}

function loadGhostEstilo(): 'contornos' | 'translucido' {
  try {
    return localStorage.getItem(GHOST_ESTILO_KEY) === 'translucido' ? 'translucido' : 'contornos'
  } catch {
    return 'contornos'
  }
}

function loadGridVisible(): boolean {
  try {
    const v = localStorage.getItem(GRID_KEY)
    if (v === null) return true
    return v !== 'false'
  } catch {
    return true
  }
}

function loadEdgesVisible(): boolean {
  try {
    const v = localStorage.getItem(EDGES_KEY)
    if (v === null) return true
    return v !== 'false'
  } catch {
    return true
  }
}

function loadBgMode(): 'dark' | 'light' {
  try {
    const v = localStorage.getItem(BG_MODE_KEY)
    return v === 'light' ? 'light' : 'dark'
  } catch {
    return 'dark'
  }
}

function loadUiScale(): number {
  try {
    const v = localStorage.getItem(UI_SCALE_KEY)
    if (v === null) return 1
    const n = Number(v)
    if (!Number.isFinite(n)) return 1
    return Math.min(UI_SCALE_MAX, Math.max(UI_SCALE_MIN, n))
  } catch {
    return 1
  }
}

/** Lê um booleano do localStorage, tolerante: qualquer valor diferente
 *  de "true" (incluindo ausência) cai no default false. */
function loadBoolDefaultFalse(key: string): boolean {
  try {
    return localStorage.getItem(key) === 'true'
  } catch {
    return false
  }
}

function loadHideSpatialVolumes(): boolean {
  try {
    const v = localStorage.getItem(HIDE_SPATIAL_KEY)
    if (v === null) return true
    return v !== 'false'
  } catch {
    return true
  }
}

function loadClassColorsEnabled(): boolean {
  try {
    const v = localStorage.getItem(CLASS_COLORS_KEY)
    if (v === null) return false
    return v === 'true'
  } catch {
    return false
  }
}


function persistBool(key: string, v: boolean) {
  try {
    localStorage.setItem(key, String(v))
  } catch {
    /* ignore */
  }
}

export const createViewerSlice: StateCreator<ViewerSlice> = (set, get) => ({
  models: [],
  activeModelId: null,
  selectedExpressId: null,
  hiddenCount: 0,
  ghostMode: false,
  ghostOpacity: loadGhostOpacity(),
  ghostEstilo: loadGhostEstilo(),
  modelosEmFantasma: [],
  setModelosEmFantasma: (ids) => set({ modelosEmFantasma: ids }),
  realce: loadRealce(),
  setRealce: (patch) => {
    const novo = { ...get().realce, ...patch }
    try {
      localStorage.setItem(REALCE_KEY, JSON.stringify(novo))
    } catch {
      /* ignore */
    }
    set({ realce: novo })
  },
  transparentCount: 0,
  transparencyOpacity: loadOpacity(TRANSPARENCY_OPACITY_KEY, 0.3),
  visibilityMode: 'normal',
  gridVisible: loadGridVisible(),
  edgesVisible: loadEdgesVisible(),
  edgeThreshold: loadEdgeThreshold(),
  classColorsEnabled: loadClassColorsEnabled(),
  bgMode: loadBgMode(),
  estiloApresentacao: loadBoolDefaultFalse(ESTILO_APRESENTACAO_KEY),
  hideSpatialVolumes: loadHideSpatialVolumes(),
  appearancePanelOpen: false,
  appearancePanelPosition: { x: 80, y: 120 },
  uiScale: loadUiScale(),
  propsAdvanced: loadBoolDefaultFalse(PROPS_ADVANCED_KEY),
  leftSidebarTab: loadLeftSidebarTab(),
  levelsOverlay: (() => {
    try {
      return localStorage.getItem(LEVELS_KEY) === 'true'
    } catch {
      return false
    }
  })(),
  gridsOverlay: (() => {
    try {
      return localStorage.getItem(GRIDS_KEY) === 'true'
    } catch {
      return false
    }
  })(),
  originMarkerOverlay: (() => {
    try {
      return localStorage.getItem(ORIGIN_MARKER_KEY) === 'true'
    } catch {
      return false
    }
  })(),
  originMarkerFocus: null,
  gridsStoreyFilter: [],
  colorByPanelOpen: false,
  colorByActive: null,
  filterPanelOpen: false,
  filterPrefill: null,
  displayBearingDeg: 0,
  bearingPick: null,
  setBearingPick: (pts) => set({ bearingPick: pts }),
  setDisplayBearing: (deg) =>
    set({ displayBearingDeg: ((deg % 360) + 360) % 360 }),
  geoBaselines: {},
  geoOverrides: {},
  geoUndo: {},
  crsMeta: {},
  crsOverrides: {},
  geoDetectedBaked: {},
  coordenadasGrandes: {},
  mapPanelOpen: false,
  mapPanelDestacado: false,
  mapToolsOpen: loadBoolDefaultFalse(MAP_TOOLS_KEY),
  basemapGroundH: null,
  basemapTerrainOn: (() => {
    try {
      return localStorage.getItem(BASEMAP_TERRAIN_KEY) === 'true'
    } catch {
      return false
    }
  })(),
  basemapExtent: loadBasemapExtent(),
  // Por defeito só a origem do ficheiro tem representação 3D: as
  // origens da hierarquia espacial são informação textual (Info do
  // modelo), e os tripés extra tornavam a cena confusa. Tudo opt-in
  // pelo filtro de origens. A escolha persiste no localStorage.
  originMarkerKinds: loadOriginKinds(),
  placementEditorRequest: null,
  basemap3dOn: false,

  setActiveModel: (id) => set({ activeModelId: id }),
  setSelectedExpressId: (id) => set({ selectedExpressId: id }),
  setHiddenCount: (count) => set({ hiddenCount: count }),
  setGhostMode: (on) => set({ ghostMode: on }),
  setGhostOpacity: (value) => {
    const clamped = Math.min(0.5, Math.max(0.02, value))
    try {
      localStorage.setItem(GHOST_OPACITY_KEY, String(clamped))
    } catch {
      /* ignore */
    }
    set({ ghostOpacity: clamped })
  },
  setGhostEstilo: (estilo) => {
    try {
      localStorage.setItem(GHOST_ESTILO_KEY, estilo)
    } catch {
      /* ignore */
    }
    set({ ghostEstilo: estilo })
  },
  setTransparentCount: (count) => set({ transparentCount: count }),
  setTransparencyOpacity: (value) => {
    const clamped = Math.min(0.9, Math.max(0.05, value))
    try {
      localStorage.setItem(TRANSPARENCY_OPACITY_KEY, String(clamped))
    } catch {
      /* ignore */
    }
    set({ transparencyOpacity: clamped })
  },
  setVisibilityMode: (mode) => set({ visibilityMode: mode }),
  setGridVisible: (on) => {
    persistBool(GRID_KEY, on)
    set({ gridVisible: on })
  },
  setEdgesVisible: (on) => {
    persistBool(EDGES_KEY, on)
    set({ edgesVisible: on })
  },
  setEdgeThreshold: (deg) => {
    const clamped = Math.min(
      EDGE_THRESHOLD_MAX,
      Math.max(EDGE_THRESHOLD_MIN, Math.round(deg)),
    )
    try {
      localStorage.setItem(EDGE_THRESHOLD_KEY, String(clamped))
    } catch {
      /* ignore */
    }
    set({ edgeThreshold: clamped })
  },
  setClassColorsEnabled: (on) => {
    persistBool(CLASS_COLORS_KEY, on)
    set({ classColorsEnabled: on })
  },
  setBgMode: (mode) => {
    try {
      localStorage.setItem(BG_MODE_KEY, mode)
    } catch {
      /* ignore */
    }
    set({ bgMode: mode })
  },
  setEstiloApresentacao: (on) => {
    persistBool(ESTILO_APRESENTACAO_KEY, on)
    set({ estiloApresentacao: on })
  },
  setHideSpatialVolumes: (on) => {
    persistBool(HIDE_SPATIAL_KEY, on)
    set({ hideSpatialVolumes: on })
  },
  setAppearancePanelOpen: (on) => set({ appearancePanelOpen: on }),
  setAppearancePanelPosition: (p) => set({ appearancePanelPosition: p }),
  setUiScale: (value) => {
    const clamped = Math.min(UI_SCALE_MAX, Math.max(UI_SCALE_MIN, value))
    try {
      localStorage.setItem(UI_SCALE_KEY, String(clamped))
    } catch {
      /* ignore */
    }
    set({ uiScale: clamped })
  },
  setPropsAdvanced: (on) => {
    persistBool(PROPS_ADVANCED_KEY, on)
    set({ propsAdvanced: on })
  },
  setLeftSidebarTab: (tab) => {
    try {
      localStorage.setItem(LEFT_TAB_KEY, tab)
    } catch {
      /* ignore */
    }
    set({ leftSidebarTab: tab })
  },
  setLevelsOverlay: (on) => {
    try {
      localStorage.setItem(LEVELS_KEY, String(on))
    } catch {
      /* ignore */
    }
    set({ levelsOverlay: on })
  },
  setOriginMarkerOverlay: (on) => {
    try {
      localStorage.setItem(ORIGIN_MARKER_KEY, String(on))
    } catch {
      /* ignore */
    }
    set(on ? { originMarkerOverlay: on } : { originMarkerOverlay: on, originMarkerFocus: null })
  },
  setOriginMarkerFocus: (foco) => set({ originMarkerFocus: foco }),
  setGridsOverlay: (on) => {
    try {
      localStorage.setItem(GRIDS_KEY, String(on))
    } catch {
      /* ignore */
    }
    set({ gridsOverlay: on })
  },
  setGridsStoreyFilter: (ids) => set({ gridsStoreyFilter: ids }),
  toggleGridStorey: (id) =>
    set((state) => {
      const next = new Set(state.gridsStoreyFilter)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return { gridsStoreyFilter: Array.from(next) }
    }),
  setColorByPanelOpen: (on) => set({ colorByPanelOpen: on }),
  setColorByActive: (c) => set({ colorByActive: c }),
  setFilterPanelOpen: (on) => set({ filterPanelOpen: on }),
  setFilterPrefill: (q) => set({ filterPrefill: q }),
  setGeoBaseline: (modelId, placement) =>
    set((state) => ({
      geoBaselines: { ...state.geoBaselines, [modelId]: placement },
    })),
  setGeoOverride: (modelId, partial) =>
    set((state) => {
      // Merge over the existing override (or the baseline if no
      // override yet). This lets the UI patch a single field
      // without forcing the caller to read+merge first.
      const prev =
        state.geoOverrides[modelId] ??
        state.geoBaselines[modelId] ??
        null
      const base = prev ?? {
        eastings: 0,
        northings: 0,
        orthogonalHeight: 0,
        rotationDeg: 0,
        scale: 1,
      }
      const next: GeoPlacement = { ...base, ...partial }
      // Guardar o estado anterior para o "Desfazer" (um nível). Quando
      // não havia nada (prev null), guardamos o baseline zero para que
      // desfazer volte a esse ponto de partida.
      return {
        geoOverrides: { ...state.geoOverrides, [modelId]: next },
        geoUndo: { ...state.geoUndo, [modelId]: base },
      }
    }),
  clearGeoOverride: (modelId) =>
    set((state) => {
      if (!(modelId in state.geoOverrides) && !(modelId in state.geoUndo)) {
        return state
      }
      const next = { ...state.geoOverrides }
      delete next[modelId]
      const nextUndo = { ...state.geoUndo }
      delete nextUndo[modelId]
      return { geoOverrides: next, geoUndo: nextUndo }
    }),
  undoGeoOverride: (modelId) =>
    set((state) => {
      const target = state.geoUndo[modelId]
      if (!target) return state
      const nextUndo = { ...state.geoUndo }
      delete nextUndo[modelId]
      return {
        geoOverrides: { ...state.geoOverrides, [modelId]: target },
        geoUndo: nextUndo,
      }
    }),
  setCrsMeta: (modelId, crsName) =>
    set((state) => ({
      crsMeta: { ...state.crsMeta, [modelId]: crsName },
    })),
  setGeoDetectedBaked: (modelId, placement) =>
    set((state) => ({
      geoDetectedBaked: { ...state.geoDetectedBaked, [modelId]: placement },
    })),
  setCoordenadasGrandes: (modelId, distanciaM) =>
    set((state) => ({
      coordenadasGrandes: { ...state.coordenadasGrandes, [modelId]: distanciaM },
    })),
  setCrsOverride: (modelId, crsCode) =>
    set((state) => ({
      crsOverrides: { ...state.crsOverrides, [modelId]: crsCode },
    })),
  clearCrsOverride: (modelId) =>
    set((state) => {
      if (!(modelId in state.crsOverrides)) return state
      const next = { ...state.crsOverrides }
      delete next[modelId]
      return { crsOverrides: next }
    }),
  setMapPanelOpen: (open) => set(open ? { mapPanelOpen: true } : { mapPanelOpen: false, mapPanelDestacado: false }),
  setMapPanelDestacado: (v) => set({ mapPanelDestacado: v }),
  setMapToolsOpen: (open) => {
    persistBool(MAP_TOOLS_KEY, open)
    set({ mapToolsOpen: open })
  },
  requestPlacementEditor: (modelId) =>
    set({ placementEditorRequest: modelId }),
  clearPlacementEditorRequest: () => set({ placementEditorRequest: null }),
  setBasemap3dOn: (on) => set({ basemap3dOn: on }),
  setBasemapGroundH: (h) => set({ basemapGroundH: h }),
  setOriginMarkerKind: (kind, on) =>
    set((state) => {
      const next = { ...state.originMarkerKinds, [kind]: on }
      try {
        localStorage.setItem(ORIGIN_KINDS_KEY, JSON.stringify(next))
      } catch {
        /* ignore */
      }
      return { originMarkerKinds: next }
    }),
  setBasemapTerrainOn: (on) => {
    try {
      localStorage.setItem(BASEMAP_TERRAIN_KEY, String(on))
    } catch {
      /* ignore */
    }
    set({ basemapTerrainOn: on })
  },
  setBasemapExtent: (extent) => {
    const clamped = Math.min(3, Math.max(1, Math.round(extent)))
    try {
      localStorage.setItem(BASEMAP_EXTENT_KEY, String(clamped))
    } catch {
      /* ignore */
    }
    set({ basemapExtent: clamped })
  },
  addModel: (model) =>
    set((state) => ({
      models: [...state.models, model],
      activeModelId: state.activeModelId ?? model.id,
    })),
  removeModel: (id) =>
    set((state) => {
      const nextBaselines = { ...state.geoBaselines }
      delete nextBaselines[id]
      const nextOverrides = { ...state.geoOverrides }
      delete nextOverrides[id]
      const nextUndo = { ...state.geoUndo }
      delete nextUndo[id]
      const nextCrsMeta = { ...state.crsMeta }
      delete nextCrsMeta[id]
      const nextCrsOverrides = { ...state.crsOverrides }
      delete nextCrsOverrides[id]
      const nextDetected = { ...state.geoDetectedBaked }
      delete nextDetected[id]
      return {
        models: state.models.filter((m) => m.id !== id),
        activeModelId:
          state.activeModelId === id ? null : state.activeModelId,
        geoBaselines: nextBaselines,
        geoOverrides: nextOverrides,
        geoUndo: nextUndo,
        crsMeta: nextCrsMeta,
        crsOverrides: nextCrsOverrides,
        geoDetectedBaked: nextDetected,
      }
    }),
  toggleModelVisibility: (id) =>
    set((state) => ({
      models: state.models.map((m) =>
        m.id === id ? { ...m, visible: !m.visible } : m,
      ),
    })),
  setModelVisibility: (id, visible) =>
    set((state) => ({
      models: state.models.map((m) =>
        m.id === id ? { ...m, visible } : m,
      ),
    })),
  setModelEntityCount: (id, entityCount) =>
    set((state) => ({
      models: state.models.map((m) =>
        m.id === id ? { ...m, entityCount } : m,
      ),
    })),
})
